import React, { useState } from "react";
import dayjs from "dayjs";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import ContractFormCst from "../ContractFormCst";
import { useFetchDetail, usePostDetail } from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL, uploadFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import ButtonBackPage from "@components/ButtonBackPage";
import { useAddEditContract } from "../hooks/useAddEditContract";

const AddContract: React.FC = () => {
  const { t } = useTranslation(["contracts-page"]);
  const { columns, actionsForForms } = useFetchDetail(
    ServicesURL.getContracts,
    undefined,
    PagesID["contracts"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetContractAssociations,
    setSupplierAssociation,
    setAccountsAssociation,
    accountsAssociation,
    supplierAssociation,
    openAssociations,
  } = useAddEditContract([]);

  const { id = 1 } = useParams();
  const navigate = useNavigate();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { trigger: triggerSupplier } = usePostDetail(ServicesURL.postContracts);

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToSend: any = {
      sap_code: inputForm.sap_code || null,
      start_date: !!inputForm.start_date
        ? dayjs(new Date(inputForm.start_date)).format("YYYY-MM-DD")
        : null,
      end_date: !!inputForm.end_date
        ? dayjs(new Date(inputForm.end_date)).format("YYYY-MM-DD")
        : null,
      nominal_value: Number(inputForm.nominal_value) || null,
      supplier_id:
        supplierAssociation && supplierAssociation.length > 0
          ? parseInt(supplierAssociation[0].id)
          : null,
      account_id:
        accountsAssociation && accountsAssociation.length > 0
          ? parseInt(accountsAssociation[0].id)
          : null,
      description: inputForm.description || null,
    };

    try {
      await triggerSupplier(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-contracts.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("add.hero-title")}
          description={t("add.hero-description")}
        />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <ContractFormCst
              columns={columns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(-1);
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              resetContractAssociations={resetContractAssociations}
              association={openAssociations}
              fullWidth={true}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddContract;
